import { getInteractionToken } from '~/api/interaction';
import { serviceErrorCodes } from '~/constants';
import type { InteractionIntent, SdkError } from '~/types/interaction';
import { resultOf } from '~/utils/fp-results';

/**
 * Ensures the SDK is initialized with the given gateway name.
 * If already initialized, this function does nothing.
 * @param gatewayName - The name of the gateway.
 * @throws Error if window.ScLoan is not available.
 */
export const ensureSdkInitialized = (gatewayName: string): void => {
  // If SDK is already initialized, do nothing
  if (window.las) {
    return;
  }

  // Check if ScLoan is available
  if (!window.ScLoan) {
    throw new Error(
      'ScLoan SDK is not available. Make sure the SDK script has loaded.',
    );
  }

  // Initialize the SDK
  window.las = new window.ScLoan({ gatewayName });
};

/**
 * Starts a new application journey.
 * @param referrer - The referrer ID for the application.
 * @returns A promise that resolves to an array containing a boolean indicating success and the response from the SDK.
 */
export const startNewApplication = async (referrer: string) => {
  const [interactionOk, interaction] = await resultOf(
    getInteractionToken({ intent: 'LAUNCH', referrer }),
  );
  if (!interactionOk) return [false, interaction];

  // Extract gateway name and ensure SDK is initialized
  ensureSdkInitialized(interaction.data.partnerName);

  const [applyOk, response] = await resultOf(
    window.las.apply({
      interactionToken: interaction.data.interactionToken,
    }),
  );
  if (!applyOk) return [false, response];

  return [true, response];
};

/**
 * Resumes an application journey.
 * @param appIntent - The intent of the application.
 * @param userId - The user ID for the application.
 * @returns A promise that resolves to an array containing a boolean indicating success and the response from the SDK.
 */
export const resumeApplication = async (
  appIntent: InteractionIntent,
  userId: string,
) => {
  const [interactionOk, interaction] = await resultOf(
    getInteractionToken({ intent: appIntent, userId }),
  );
  if (!interactionOk) return [false, interaction];

  // Extract gateway name and ensure SDK is initialized
  ensureSdkInitialized(interaction.data.partnerName);

  const [applyOk, response] = await resultOf(
    window.las.apply({
      interactionToken: interaction.data.interactionToken,
    }),
  );
  if (!applyOk) return [false, response];

  return [true, response];
};

/**
 * Opens the service dashboard.
 * @param userId - The user ID for the service dashboard.
 * @param lId - The loan ID for the service dashboard.
 * @param target - The target of the service dashboard.
 * @param onTopupTriggered - The function to call when the topup is triggered.
 * @returns A promise that resolves to an array containing a boolean indicating success and the response from the SDK.
 */
export const openServicingDashboard = async ({
  userId,
  lId,
  target,
  onTopupTriggered,
}: {
  userId?: string;
  lId?: string;
  target: string;
  onTopupTriggered: (userId: string) => void;
}) => {
  const [interactionOk, interaction] = await resultOf(
    getInteractionToken({ intent: 'SERVICE', userId, lId }),
  );
  if (!interactionOk) return [false, interaction];

  // Extract gateway name and ensure SDK is initialized
  ensureSdkInitialized(interaction.data.partnerName);

  const serviceResult = window.las.service({
    interactionToken: interaction.data.interactionToken,
    target,
  });

  const { task } = serviceResult;
  const [taskOk, response] = await resultOf(task);

  if (!taskOk) {
    // fire the topup flow in case of topup triggered error code
    if ((response as SdkError)?.code === serviceErrorCodes.TOPUP_TRIGGERED) {
      const userId = (response as SdkError).data.userId;
      onTopupTriggered(userId);
    }

    return [false, response];
  }
  return [true, response];
};
